import { useEffect, useRef } from 'react';

import logger from 'utils/logger';

export type OfferLoadBlocker =
  | 'authentication'
  | 'offer-query'
  | 'cms'
  | 'menu'
  | 'walmart'
  | 'reduction'
  | 'hydration';

export type OffersPresentationState =
  | { status: 'initial-loading'; blockers: readonly OfferLoadBlocker[] }
  | { status: 'waiting-for-network'; blockers: readonly OfferLoadBlocker[] }
  | { status: 'ready' | 'empty'; isRefreshing: boolean; refreshError?: Error }
  | { status: 'error'; error: Error; blockers: readonly OfferLoadBlocker[] };

export interface OffersPresentationInputs {
  loading: boolean;
  error?: Error;
  hasSettled: boolean;
  hasContent: boolean;
  isInternetReachable: boolean;
  blockers?: readonly OfferLoadBlocker[];
}

export const resolveOffersPresentationState = ({
  loading,
  error,
  hasSettled,
  hasContent,
  isInternetReachable,
  blockers = [],
}: OffersPresentationInputs): OffersPresentationState => {
  if (hasSettled) {
    return {
      status: hasContent ? 'ready' : 'empty',
      isRefreshing: loading,
      ...(error ? { refreshError: error } : {}),
    };
  }
  if (!isInternetReachable) {
    return { status: 'waiting-for-network', blockers };
  }
  if (error) {
    return { status: 'error', error, blockers };
  }
  return { status: 'initial-loading', blockers };
};

interface UseOffersPresentationStateOptions extends OffersPresentationInputs {
  runtime: 'legacy' | 'loyalty-service';
  enteredWithPromoCode: boolean;
  storeId?: string;
  serviceMode?: string;
}

export const useOffersPresentationState = (options: UseOffersPresentationStateOptions) => {
  const state = resolveOffersPresentationState(options);
  const lastDiagnosticStatus = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (state.status !== 'waiting-for-network' && state.status !== 'error') {
      lastDiagnosticStatus.current = undefined;
      return;
    }
    if (lastDiagnosticStatus.current === state.status) {
      return;
    }
    lastDiagnosticStatus.current = state.status;
    logger.warn({
      message: 'Offers list entered a recoverable load state',
      offersLoadStatus: state.status,
      offersRuntime: options.runtime,
      offersRouteEntry: options.enteredWithPromoCode ? 'promo-code' : 'ordinary',
      offersLoadBlockers: state.blockers,
      storeId: options.storeId,
      serviceMode: options.serviceMode,
    });
  }, [options.enteredWithPromoCode, options.runtime, options.serviceMode, options.storeId, state]);

  return state;
};
